// ============================================================================
// FOLIO SERVICE (PMS Phase 2 slice 5)
// ============================================================================
// crs.Api.Controllers.FolioModule.FolioController -> /api/v1/Folio
// In-House Guests, the folio screen, desk charges, reversals and room charging.
// Receipts go through paymentService (they reach the folio in the same transaction).

import { get, post, put } from '../client/axiosClient';
import { buildEndpointWithParams, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, ID } from '../types';
import type {
  FolioChargePreview,
  FolioDetail,
  FolioPostingControlRequest,
  FolioPostingResult,
  InHouseFolioQuery,
  InHouseFolioRow,
  PostFolioChargeRequest,
} from '../types/folio.types';

const BASE = '/Folio';

export interface InHousePage {
  items: InHouseFolioRow[];
  totalCount: number;
}

const unwrap = <T,>(res: { data?: ApiResponse<T> }, fallback: string): T => {
  if (res.data?.data === undefined || res.data?.data === null) throw new Error(res.data?.message || fallback);
  return res.data.data;
};

/**
 * A folio refusal (closed, room charging off, credit limit) comes back as 409 with
 * the posting result in `data` — returned, not thrown, so the screen can say what
 * the desk should do. Anything else is thrown as usual.
 */
const postingCall = async (call: () => Promise<{ data?: ApiResponse<FolioPostingResult> }>): Promise<FolioPostingResult> => {
  try {
    return unwrap(await call(), 'The folio did not answer.');
  } catch (err: any) {
    const body = err?.response?.data;
    if (err?.response?.status === 409 && body?.data && body.data.refusal) return body.data as FolioPostingResult;
    throw err;
  }
};

class FolioService {
  async inHouse(query: InHouseFolioQuery): Promise<InHousePage> {
    const res = await get<ApiResponse<{ items: InHouseFolioRow[]; totalCount: number }>>(
      buildEndpointWithQuery(`${BASE}/in-house`, { ...query }));
    return { items: res.data?.data?.items ?? [], totalCount: res.data?.data?.totalCount ?? 0 };
  }

  async get(folioId: ID): Promise<FolioDetail> {
    const res = await get<ApiResponse<FolioDetail>>(buildEndpointWithParams(`${BASE}/:id`, { id: folioId }));
    return unwrap(res, 'Folio not found.');
  }

  /** The booking's latest folio, in full. Used right after check-in, when only the booking is known. */
  async getByBooking(bookingId: ID): Promise<FolioDetail> {
    const res = await get<ApiResponse<{ id: string }>>(buildEndpointWithParams(`${BASE}/by-booking/:bookingId`, { bookingId }));
    return this.get(unwrap(res, 'No folio was found for the booking.').id);
  }

  async previewCharge(folioId: ID, request: PostFolioChargeRequest): Promise<FolioChargePreview> {
    const res = await post<ApiResponse<FolioChargePreview>>(
      buildEndpointWithParams(`${BASE}/:id/charges/preview`, { id: folioId }), request);
    return unwrap(res, 'Preview failed.');
  }

  postCharge(folioId: ID, request: PostFolioChargeRequest): Promise<FolioPostingResult> {
    return postingCall(() => post<ApiResponse<FolioPostingResult>>(
      buildEndpointWithParams(`${BASE}/:id/charges`, { id: folioId }), request));
  }

  reverseCharge(chargeId: ID, reason: string): Promise<FolioPostingResult> {
    return postingCall(() => post<ApiResponse<FolioPostingResult>>(
      buildEndpointWithParams(`${BASE}/charges/:id/reverse`, { id: chargeId }), { reason }));
  }

  /** A Folio Charge Voucher as a PDF (slice 7c), for the guest to sign. */
  async voucherPdf(voucherId: ID): Promise<Blob> {
    const res = await get<Blob>(buildEndpointWithParams(`${BASE}/vouchers/:id/pdf`, { id: voucherId }), { responseType: 'blob' });
    return res.data as Blob;
  }

  async setPostingControl(folioId: ID, request: FolioPostingControlRequest): Promise<FolioDetail> {
    const res = await put<ApiResponse<FolioDetail>>(
      buildEndpointWithParams(`${BASE}/:id/posting-control`, { id: folioId }), request);
    return unwrap(res, 'Update failed.');
  }
}

export const folioService = new FolioService();
